import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { initDonutComparison } from "./charts.js";
import {
  getCurrentViewportTier,
  isMobileTier,
  isMobileOrTabletTier,
} from "./viewport.js";

gsap.registerPlugin(ScrollTrigger);

// =====================================================================
// Module constants
// =====================================================================

const STACKING_CARD_TOP_RATIO = 0.25;
const STACKING_ROTATE_FALLBACK = [0, 4, -4];
const STACKING_AXIS_FALLBACK = ["0em", "0em", "0em"];
const RESIZE_DEBOUNCE_MS = 250;

const STEPS_VISIBLE_CARDS_DESKTOP = 3;

const ILLUSTRATIONS_STAGGER_MS = 250;

// =====================================================================
// Generic helpers
// =====================================================================

function debounceOnWidthChange(fn, ms) {
  let last = innerWidth;
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => {
      if (innerWidth !== last) {
        last = innerWidth;
        fn.apply(this, args);
      }
    }, ms);
  };
}

function setWillChange(el, value = "transform") {
  if (el) el.style.willChange = value;
}

function clearWillChange(el) {
  if (el) el.style.willChange = "";
}

function setWillChangeAll(els, value = "transform") {
  els.forEach((el) => setWillChange(el, value));
}

function clearWillChangeAll(els) {
  els.forEach(clearWillChange);
}

// =====================================================================
// Stacking sticky cards — parsers & teardown helpers
// =====================================================================

function parseRotateValues(section, attr) {
  const values = (section.getAttribute(attr) || "")
    .split(",")
    .map((val) => parseFloat(val.trim()));
  return values.length >= 1 && values.every((v) => !isNaN(v))
    ? values
    : STACKING_ROTATE_FALLBACK;
}

function parseAxisValues(section, attr) {
  const raw = section.getAttribute(attr);
  if (!raw) return STACKING_AXIS_FALLBACK;
  const values = raw
    .split(",")
    .map((val) => val.trim())
    .filter((val) => val !== "");
  return values.length ? values : STACKING_AXIS_FALLBACK;
}

function isStackingEnabledForTier(section, tier) {
  if (tier === "desktop") {
    return section.dataset.stackingCardsDesktop === "true";
  }
  if (tier === "tablet") {
    return section.dataset.stackingCardsTablet === "true";
  }
  if (isMobileTier(tier)) {
    return section.dataset.stackingCardsMobile === "true";
  }
  return false;
}

function killTriggersWithin(sections) {
  ScrollTrigger.getAll().forEach((trigger) => {
    sections.forEach((section) => {
      if (section.contains(trigger.trigger)) trigger.kill();
    });
  });
}

function resetStackingSection(section) {
  section.querySelectorAll("[data-stacking-card-target]").forEach((el) => {
    gsap.killTweensOf(el);
    gsap.set(el, { clearProps: "transform,opacity,filter" });
    clearWillChange(el);
    el.style.minHeight = "";
  });
  section.querySelectorAll("[data-stacking-card]").forEach((card) => {
    card.style.position = "";
    card.style.top = "";
    card.style.left = "";
    card.style.width = "";
    card.style.minHeight = "";
  });
  const wrapper = section.querySelector(".stacking-cards");
  if (wrapper) {
    wrapper.style.height = "";
    wrapper.style.position = "";
  }
}

// =====================================================================
// Stacking sticky cards — initialiser
// =====================================================================

/**
 * Initialise every `[data-stacking-cards-init]` section on the page.
 *
 * Builds GSAP/ScrollTrigger instances appropriate to the current viewport
 * tier (`desktop`, `tablet`, `mobile-portrait`, `mobile-landscape`). On
 * desktop/tablet cards stack vertically; on mobile they slide in
 * horizontally inside a single pinned section.
 *
 * Safe to call multiple times — existing tweens and triggers within each
 * section are killed and styles are cleared before re-initialising.
 */
export function initStackingStickyCards() {
  const cardsSections = document.querySelectorAll("[data-stacking-cards-init]");
  const currentTier = getCurrentViewportTier();
  window.viewportTier = currentTier;

  killTriggersWithin(cardsSections);
  cardsSections.forEach(resetStackingSection);

  cardsSections.forEach((section) => {
    if (!isStackingEnabledForTier(section, currentTier)) return;

    const cards = Array.from(section.querySelectorAll("[data-stacking-card]"));
    if (!cards.length) return;

    const rotateValues = parseRotateValues(
      section,
      `data-stacking-cards-${currentTier}-rotate`,
    );
    const xValues = parseAxisValues(
      section,
      `data-stacking-cards-${currentTier}-x`,
    );
    const total = cards.length;

    // --- Read pass: cache target elements & measure tallest card -------
    const targets = cards.map((card) =>
      card.querySelector("[data-stacking-card-target]"),
    );

    // Clear any stale min-height from previous init cycles so the
    // intrinsic offsetHeight is what's measured below.
    cards.forEach((c) => {
      c.style.minHeight = "";
      const t = c.querySelector("[data-stacking-card-target]");
      if (t) t.style.minHeight = "";
    });

    const maxCardHeight = Math.max(...cards.map((c) => c.offsetHeight));

    // --- Write pass: equalise card heights -----------------------------
    cards.forEach((card, i) => {
      card.style.minHeight = `${maxCardHeight}px`;
      if (targets[i]) targets[i].style.minHeight = `${maxCardHeight}px`;
    });

    const STACK_TOP = STACKING_CARD_TOP_RATIO;
    const SCROLL_PER_CARD = maxCardHeight;

    // ================================================================
    // MOBILE — horizontal stacking cards
    // ================================================================
    if (isMobileOrTabletTier(currentTier)) {
      const wrapper = section.querySelector(".stacking-cards");
      if (wrapper) {
        wrapper.style.height = `${maxCardHeight}px`;
        wrapper.style.position = "relative";
      }

      cards.forEach((card, i) => {
        card.style.position = "absolute";
        card.style.top = "0";
        card.style.left = "0";
        card.style.width = "100%";

        const targetEl = targets[i];
        if (!targetEl) return;

        gsap.set(targetEl, {
          zIndex: i + 1,
          x: i === 0 ? 0 : "100vw",
        });
      });

      cards.forEach((card, index) => {
        if (index === 0) return;

        const targetEl = targets[index];
        if (!targetEl) return;

        gsap.fromTo(
          targetEl,
          { x: "100vw" },
          {
            x: 0,
            rotate: rotateValues[index % rotateValues.length],
            ease: "none",
            scrollTrigger: {
              trigger: section,
              start: () => `top+=${SCROLL_PER_CARD * (index - 1)} top`,
              end: () => `top+=${SCROLL_PER_CARD * index} top`,
              scrub: true,
              invalidateOnRefresh: true,
              // markers: true,
              onToggle: (self) => {
                if (self.isActive) setWillChange(targetEl);
                else clearWillChange(targetEl);
              },
            },
          },
        );
      });

      // Single pin on the section for the whole stacking sequence.
      ScrollTrigger.create({
        trigger: section,
        start: "top 12%",
        end: () => `+=${SCROLL_PER_CARD * total}`,
        pin: true,
        pinSpacing: true,
        anticipatePin: 0,
        invalidateOnRefresh: true,
      });

      return;
    }

    // ================================================================
    // DESKTOP / TABLET — vertical stacking
    // ================================================================
    cards.forEach((card, index) => {
      const targetEl = targets[index];
      if (!targetEl) return;

      gsap.set(targetEl, { zIndex: index + 1 });

      const cardsAbove = total - index;
      const pinEnd = () => `+=${SCROLL_PER_CARD * Math.max(cardsAbove, 1)}`;

      ScrollTrigger.create({
        trigger: card,
        start: `top ${STACK_TOP * 100}%`,
        end: pinEnd,
        pin: true,
        pinSpacing: false,
        anticipatePin: 0,
        invalidateOnRefresh: true,
        // markers: true,
      });

      if (cardsAbove >= 0) {
        gsap.fromTo(
          targetEl,
          { y: 0, scale: 1, rotate: 0, x: 0 },
          {
            y: `${-15 * (cardsAbove + 1)}vh`,
            rotate: rotateValues[index % rotateValues.length],
            x: xValues[index % xValues.length],
            scale: 1 - cardsAbove * 0.04,
            ease: "none",
            scrollTrigger: {
              trigger: card,
              start: `top ${STACK_TOP * 100}%`,
              end: () => `+=${SCROLL_PER_CARD * Math.max(cardsAbove, 1)}`,
              scrub: true,
              invalidateOnRefresh: true,
              onToggle: (self) => {
                if (self.isActive) setWillChange(targetEl);
                else clearWillChange(targetEl);
              },
            },
          },
        );
      }
    });
  });

  attachStackingResizeListener(cardsSections);
}

function attachStackingResizeListener(cardsSections) {
  if (window._hasStackingResizeListener) return;

  let last = getCurrentViewportTier();
  window.addEventListener(
    "resize",
    debounceOnWidthChange(() => {
      const next = getCurrentViewportTier();
      if (last !== next) {
        ScrollTrigger.getAll().forEach((t) => t.kill());
        cardsSections.forEach((section) => {
          section
            .querySelectorAll("[data-stacking-card-target]")
            .forEach((el) => {
              gsap.killTweensOf(el);
              gsap.set(el, { clearProps: "transform,opacity,filter" });
              clearWillChange(el);
            });
        });
        initStackingStickyCards();
        // Resize is user-driven and happens long after first paint —
        // no 1000ms delay needed here; layout is already stable.
        ScrollTrigger.sort();
        ScrollTrigger.refresh();
      }
      last = next;
      window.viewportTier = next;
    }, RESIZE_DEBOUNCE_MS),
  );
  window._hasStackingResizeListener = true;
}

// =====================================================================
// Steps block — horizontal scrubbed scroller
// =====================================================================
function initStepsBlock() {
  const stepsBlock = document.querySelector(".steps-block");
  const wrapper = document.querySelector(".steps-block__track-wrapper");
  const track = document.querySelector(".steps-block__track");
  if (!stepsBlock || !track || !wrapper) return;

  // ── Mobile + tablet: fade-in por scroll ─────────────────
  if (isMobileOrTabletTier(getCurrentViewportTier())) {
    track.querySelectorAll(".steps-block__step").forEach((card) => {
      gsap.set(card, { opacity: 0.4, scale: 0.95 });
      gsap.to(card, {
        opacity: 1,
        scale: 1,
        duration: 0.4,
        ease: "power2.out",
        scrollTrigger: {
          trigger: card,
          start: "top 75%",
          toggleActions: "play none none reverse",
        },
      });
    });
    return;
  }

  // ── Desktop: horizontal scrub ───────────────────────────────────────
  const container = stepsBlock.querySelector(".container");
  const cs = getComputedStyle(container);
  const gap = parseFloat(getComputedStyle(track).gap) || 0;
  const availW =
    container.offsetWidth -
    parseFloat(cs.paddingLeft) -
    parseFloat(cs.paddingRight);

  const cards = [...track.querySelectorAll(".steps-block__step")];
  const cardWidth =
    (availW - gap * (STEPS_VISIBLE_CARDS_DESKTOP - 1)) /
    STEPS_VISIBLE_CARDS_DESKTOP;

  cards.forEach((c) => {
    c.style.width = `${cardWidth}px`;
    gsap.set(c, { opacity: 0.4, scale: 0.95 });
  });
  gsap.set(cards[0], { opacity: 1, scale: 1 });

  const DWELL_PX = 220;
  const trackScroll = track.scrollWidth - availW;
  const SCROLL_TOTAL = trackScroll + DWELL_PX;

  gsap.to(track, {
    x: () => -trackScroll,
    ease: "none",
    scrollTrigger: {
      trigger: stepsBlock,
      start: "top 20%",
      end: () => `+=${SCROLL_TOTAL}`,
      scrub: 0.6,
      pin: true,
      pinSpacing: true,
      fastScrollEnd: true,
      preventOverlaps: true,
      invalidateOnRefresh: true,
      onToggle: (self) => {
        self.isActive ? setWillChange(track) : clearWillChange(track);
      },
      onUpdate: (self) => {
        const p = self.progress;

        const trackProgress = Math.min(p / (trackScroll / SCROLL_TOTAL), 1);

        cards.forEach((card, i) => {
          const activationThreshold = i / (cards.length - 1);
          const isActive = trackProgress >= activationThreshold - 0.01;

          gsap.to(card, {
            opacity: isActive ? 1 : 0.4,
            scale: isActive ? 1 : 0.95,
            duration: 0.3,
            ease: "power2.out",
            overwrite: true,
          });
        });
      },
    },
  });
}

// =====================================================================
// Illustrations block — staggered reveal on intersect
// =====================================================================

/**
 * Reveals `.illustrations-block__item` elements as they enter the viewport,
 * staggered by their vertical position to produce a top-down cascade.
 */
export function initIllustrationsBlock() {
  const items = document.querySelectorAll(".illustrations-block__item");
  if (!items.length) return;

  const observer = new IntersectionObserver(
    (entries) => {
      // Sort by vertical position so the cascade always runs top-to-bottom,
      // regardless of the order entries arrive from the observer.
      const visible = entries.filter((e) => e.isIntersecting);
      visible
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        .forEach((entry, i) => {
          setTimeout(() => {
            entry.target.classList.add("is-visible");
          }, i * ILLUSTRATIONS_STAGGER_MS);
          observer.unobserve(entry.target);
        });
    },
    {
      threshold: 0,
      rootMargin: "0px 0px -50px 0px",
    },
  );

  items.forEach((item) => observer.observe(item));
}

// =====================================================================
// Logo-text sections — title/description + logo intros
// =====================================================================

function initLogoTextSections() {
  document.querySelectorAll(".logo-text").forEach((section) => {
    const title = section.querySelector(".logo-text__title");
    const description = section.querySelector(".logo-text__description");
    const logos = section.querySelectorAll("[data-logo]");
    const headerEls = [title, description].filter(Boolean);

    gsap.from([title, description], {
      scrollTrigger: {
        trigger: section,
        start: "top 80%",
        end: "top 40%",
        // markers: true,
      },
      y: 30,
      opacity: 0,
      duration: 0.6,
      stagger: 0.15,
      ease: "power2.out",
      onStart: () => setWillChangeAll(headerEls, "transform, opacity"),
      onComplete: () => clearWillChangeAll(headerEls),
    });

    if (logos.length) {
      gsap.set(logos, { scale: 0, opacity: 0 });

      gsap.to(logos, {
        scrollTrigger: {
          trigger: section,
          start: "top 40%",
          end: "top 20%",
        },
        scale: 1,
        opacity: 1,
        duration: 0.5,
        stagger: 0.08,
        ease: "back.out(1.4)",
        onStart: () => setWillChangeAll(logos, "transform, opacity"),
        onComplete: () => clearWillChangeAll(logos),
      });
    }
  });
}

// =====================================================================
// Blockquote intros
// =====================================================================

function initBlockquotes() {
  gsap.utils.toArray("[data-blockquote]").forEach((el) => {
    const quote = el.querySelector(".blockquote__text");
    const quoteMarks = el.querySelectorAll(".blockquote__quote-mark");
    const markPair = [quoteMarks[0], quoteMarks[1]].filter(Boolean);

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: el,
        start: "top 80%",
        toggleActions: "play none none reverse",
      },
      onStart: () => {
        setWillChange(quote, "opacity");
        setWillChangeAll(markPair, "transform, opacity");
      },
      onComplete: () => {
        clearWillChange(quote);
        clearWillChangeAll(markPair);
      },
    });
    tl.from(quote, {
      opacity: 0,
      duration: 1.2,
      ease: "expo.out",
    }).from(
      [quoteMarks[0], quoteMarks[1]],
      {
        scale: 0,
        opacity: 0,
        duration: 0.8,
        ease: "back.out(1.7)",
      },
      "<0.2",
    );
  });
}

// =====================================================================
// Testimonial carousel — horizontal stacking deck
// =====================================================================
const TESTIMONIAL_ROTATE_DEG = 3;
const TESTIMONIAL_JITTER_Y = 6;
const TESTIMONIAL_STACK_SHADOW = "0 12px 24px rgba(0, 0, 0, 0.35)";
const TESTIMONIAL_BASE_SHADOW = "0 2px 6px rgba(0, 0, 0, 0.08)";
const TESTIMONIAL_DURATION = 0.5;
const TESTIMONIAL_EASE = "power3.out";

/**
 * Initialise every `[data-testimonial-carousel]` section on the page.
 *
 * Cards slide in from a horizontal row and stack to the left as the user
 * advances. The first card is the fixed base and never receives the stack
 * shadow; every card at index <= current (and > 0) stacks with a slight
 * rotation/jitter and the elevated shadow.
 *
 * The stack offset is computed so the fanned-out cards span the full width
 * of the row without overflowing (worst case: all cards stacked but the
 * active one). Recomputed on resize.
 *
 * Safe to call multiple times — listeners are stored per section so a prior
 * init is torn down before rebinding.
 */
function initTestimonialCarousels() {
  const sections = document.querySelectorAll("[data-testimonial-carousel]");
  const MOBILE_QUERY = "(max-width: 767px)";

  sections.forEach((section) => {
    // Tear down a previous init on this section, if any.
    if (section._testimonialTeardown) section._testimonialTeardown();

    const row = section.querySelector("[data-testimonial-row]");
    const prevBtn = section.querySelector("[data-testimonial-prev]");
    const nextBtn = section.querySelector("[data-testimonial-next]");
    if (!row) return;

    const cards = Array.from(row.children);
    const total = cards.length;
    if (!total) return;

    let current = 0;
    let cardStep = 0;
    let stackOffsetX = 0;
    let mobileEnterOffsetX = 0;

    function isMobile() {
      return window.matchMedia(MOBILE_QUERY).matches;
    }

    function measure() {
      if (isMobile()) {
        // On mobile the cards stack in place (no horizontal fan). Cards not
        // yet revealed wait off the right edge of the track, ready to slide
        // in when they become current. The +48px buffer clears the card's
        // own box-shadow (which paints beyond its border box) so no sliver
        // of the waiting pile bleeds into view while it's supposed to be
        // fully offscreen.
        cardStep = 0;
        stackOffsetX = 0;
        mobileEnterOffsetX =
          (row.getBoundingClientRect().width ||
            (cards[0] ? cards[0].getBoundingClientRect().width : 0)) + 48;
        return;
      }

      if (total > 1) {
        cardStep =
          cards[1].getBoundingClientRect().left -
          cards[0].getBoundingClientRect().left;
      } else {
        cardStep = cards[0].getBoundingClientRect().width;
      }

      // The fan of stacked cards should span the full row width. Worst case
      // is every card stacked except the active one: total - 1 cards.
      // Subtract one card width so the last stacked card's right edge lands
      // on the row edge instead of overflowing.
      const stackedCount = Math.max(total - 1, 1);
      const rowWidth = row.getBoundingClientRect().width;
      const cardWidth = cards[0].getBoundingClientRect().width;
      stackOffsetX = (rowWidth - cardWidth) / stackedCount;
    }

    function applyState(instant) {
      const mobile = isMobile();

      cards.forEach((card, i) => {
        const innerCard = card.querySelector(".testimonial_card");

        let target;
        let revealed = true;
        if (mobile) {
          // Mobile: reveal order determines stacking, not the static index.
          // Cards not yet revealed must render BELOW everything (and wait
          // off-screen to the right), otherwise the highest-index card
          // (still unrevealed) would always render on top and hide the pile.
          revealed = i <= current;

          if (i === 0) {
            target = {
              x: 0,
              y: 0,
              rotation: 0,
              boxShadow: TESTIMONIAL_BASE_SHADOW,
            };
          } else if (revealed) {
            // Alternate the tilt per card so the pile looks like loose stacked
            // cards, not every single one leaning the same way.
            const sign = i % 2 === 0 ? -1 : 1;
            target = {
              x: 0,
              y: 0,
              rotation: sign * TESTIMONIAL_ROTATE_DEG,
              boxShadow: TESTIMONIAL_STACK_SHADOW,
            };
          } else {
            target = {
              x: mobileEnterOffsetX,
              y: 0,
              rotation: 0,
              boxShadow: TESTIMONIAL_BASE_SHADOW,
            };
          }

          if (revealed || instant) {
            // Coming forward (or an instant snap): raise/lower the stacking
            // order immediately, it reads correctly from the first frame.
            card.style.zIndex = revealed ? i : -1;
          }
          // Going backward and animated: leave the z-index as it was (still
          // on top) until the exit finishes — otherwise it ducks behind the
          // pile the instant you click prev, instead of visibly sliding back
          // out over it. It gets dropped to -1 in onComplete below.
        } else {
          card.style.zIndex = i;
          if (i === 0) {
            target = {
              x: 0,
              y: 0,
              rotation: 0,
              boxShadow: TESTIMONIAL_BASE_SHADOW,
            };
          } else if (i <= current) {
            const sign = i % 2 === 0 ? -1 : 1;
            target = {
              x: -(i * cardStep) + i * stackOffsetX,
              y: sign * TESTIMONIAL_JITTER_Y,
              rotation: sign * TESTIMONIAL_ROTATE_DEG,
              boxShadow: TESTIMONIAL_STACK_SHADOW,
            };
          } else {
            target = {
              x: -(current * cardStep) + current * stackOffsetX,
              y: 0,
              rotation: 0,
              boxShadow: TESTIMONIAL_BASE_SHADOW,
            };
          }
        }

        if (instant) {
          gsap.set(card, {
            x: target.x,
            y: target.y,
            rotation: target.rotation,
          });
          if (innerCard) gsap.set(innerCard, { boxShadow: target.boxShadow });
        } else {
          setWillChange(card);
          gsap.to(card, {
            x: target.x,
            y: target.y,
            rotation: target.rotation,
            duration: TESTIMONIAL_DURATION,
            ease: TESTIMONIAL_EASE,
            onComplete: () => {
              clearWillChange(card);
              if (mobile && !revealed) card.style.zIndex = -1;
            },
          });
          if (innerCard) {
            gsap.to(innerCard, {
              boxShadow: target.boxShadow,
              duration: TESTIMONIAL_DURATION,
              ease: TESTIMONIAL_EASE,
            });
          }
        }
      });

      if (prevBtn) prevBtn.disabled = current === 0;
      if (nextBtn) nextBtn.disabled = current === total - 1;
    }

    function next() {
      if (current < total - 1) {
        current++;
        applyState(false);
      }
    }

    function prev() {
      if (current > 0) {
        current--;
        applyState(false);
      }
    }

    const onResize = debounceOnWidthChange(() => {
      measure();
      applyState(true);
    }, RESIZE_DEBOUNCE_MS);

    if (nextBtn) nextBtn.addEventListener("click", next);
    if (prevBtn) prevBtn.addEventListener("click", prev);
    window.addEventListener("resize", onResize);

    measure();
    applyState(true);

    // Store teardown so a re-init (or SPA unmount) can unbind cleanly.
    section._testimonialTeardown = () => {
      if (nextBtn) nextBtn.removeEventListener("click", next);
      if (prevBtn) prevBtn.removeEventListener("click", prev);
      window.removeEventListener("resize", onResize);
      cards.forEach((card) => gsap.killTweensOf(card));
      section._testimonialTeardown = null;
    };
  });
}

// =====================================================================
// Login image slider
// =====================================================================

function initLoginImageSlider() {
  const wrapper = document.querySelector(".form_image_wrapper");
  if (!wrapper) return;
  if (!wrapper.offsetParent) return;

  const images = wrapper.querySelectorAll(".form_image");
  if (images.length <= 1) return;

  const svgs = wrapper.querySelectorAll(".top-corner, .bottom-corner");
  if (svgs.length <= 1) return;

  const corners = {
    topLeft: wrapper.querySelector(".form_top_left_corner"),
    topRight: wrapper.querySelector(".form_top_right_corner"),
    bottomRight: wrapper.querySelector(".form_bottom_right_corner"),
    bottomLeft: wrapper.querySelector(".form_bottom_left_corner"),
  };

  const pairs = [
    [corners.topLeft, corners.bottomRight],
    [corners.topRight, corners.bottomLeft],
  ];

  function randomSize() {
    return {
      width: Math.max(96, gsap.utils.random(96, 220, 1)),
      height: Math.max(96, gsap.utils.random(96, 140, 1)),
    };
  }

  gsap.set(corners.topLeft, { transformOrigin: "top left" });
  gsap.set(corners.topRight, { transformOrigin: "top right" });
  gsap.set(corners.bottomRight, { transformOrigin: "bottom right" });
  gsap.set(corners.bottomLeft, { transformOrigin: "bottom left" });

  let current = 0;
  let currentPair = 0;

  gsap.set(pairs[0], { width: 96, height: 96, scale: 1 });
  gsap.set(pairs[1], { width: 0, height: 0, scale: 0 });
  gsap.set(images[0], { opacity: 1 });
  images.forEach((img, i) => {
    if (i !== 0) gsap.set(img, { opacity: 0 });
  });

  setInterval(() => {
    const next = (current + 1) % images.length;
    const nextPair = (currentPair + 1) % pairs.length;
    const tl = gsap.timeline();

    tl.to(images[current], { opacity: 0, duration: 0.6, ease: "power2.inOut" });
    tl.to(
      images[next],
      { opacity: 1, duration: 0.6, ease: "power2.inOut" },
      "<",
    );

    tl.to(
      pairs[currentPair],
      {
        scale: 0,
        duration: 0.5,
        ease: "power2.in",
      },
      "<",
    );
    pairs[nextPair].forEach((corner) => {
      const size = randomSize();
      tl.fromTo(
        corner,
        { scale: 0, width: size.width, height: size.height },
        { scale: 1, duration: 0.5, ease: "power2.out" },
        "<",
      );
    });

    current = next;
    currentPair = nextPair;
  }, 5000);
}

// =====================================================================
// Bootstrap
// =====================================================================

let hasBootstrapped = false;

async function runAllInits() {
  if (hasBootstrapped) return;
  hasBootstrapped = true;

  initStackingStickyCards();
  initStepsBlock();
  initIllustrationsBlock();
  initLogoTextSections();
  initTestimonialCarousels();
  initBlockquotes();
  initLoginImageSlider();
  // initDonutComparison resolves only after Chart.js has rendered once,
  // so by this point every measurable element on the page has its final
  // size and ScrollTrigger.refresh() will read correct positions.
  await initDonutComparison();

  ScrollTrigger.sort();
  ScrollTrigger.refresh();
}

function waitForImages() {
  const pending = Array.from(document.images).filter((img) => !img.complete);
  if (!pending.length) return Promise.resolve();

  return Promise.all(
    pending.map(
      (img) =>
        new Promise((resolve) => {
          img.addEventListener("load", resolve, { once: true });
          img.addEventListener("error", resolve, { once: true });
        }),
    ),
  );
}

const fontsReady =
  document.fonts && document.fonts.ready
    ? document.fonts.ready
    : Promise.resolve();

// master.js is loaded synchronously in <head>, so this module evaluates
// before <body> has been parsed. Defer the image wait until the DOM is
// ready so `document.images` actually contains every <img> on the page.
function bootstrap() {
  Promise.all([fontsReady, waitForImages()]).then(runAllInits);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", bootstrap, { once: true });
} else {
  bootstrap();
}

// Safety net: if anything above stalls (e.g. a never-resolving image),
// the native load event still kicks the inits off exactly once.
window.addEventListener("load", runAllInits);
